"use client";

import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { RefObject, useEffect, useRef, useState } from "react";

interface ControllableDemoProps {
  containerRef: RefObject<HTMLDivElement>;
  onShowDownloadChange?: (show: boolean) => void;
}

export function ControllableDemo({ containerRef, onShowDownloadChange }: ControllableDemoProps) {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end end"],
  });

  // 视频播放状态
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // Typing animation state
  const typingText = "Okay, Please check reddit instead of twitter";
  const [displayedText, setDisplayedText] = useState("");

  // 基于滚动进度控制视频显示 - 第二阶段 (0.7-1.0, 占总进度30%)
  const showVideo = useTransform(scrollYProgress, [0.7, 0.75, 0.9, 1.0], [0, 1, 1, 1]);

  // 视频播放控制 - 基于 showVideo motion value
  useEffect(() => {
    const unsubscribe = showVideo.on("change", (value) => {
      const shouldPlay = value > 0;

      if (videoRef.current) {
        if (shouldPlay && !videoPlaying) {
          videoRef.current.currentTime = 0;
          videoRef.current.play();
          setVideoPlaying(true);
        } else if (!shouldPlay && videoPlaying) {
          videoRef.current.pause();
          setVideoPlaying(false);
        }
      }

      // 通知父组件视频状态变化
      if (onShowDownloadChange) {
        onShowDownloadChange(shouldPlay);
      }
    });

    return () => unsubscribe();
  }, [videoPlaying, onShowDownloadChange, showVideo]);

  // Animation transforms for workflow steps - 扩展时间并调整时机以适应0.7目标
  // Workflow container appears (0-0.08) - 扩展时间
  const workflowOpacity = useTransform(scrollYProgress, [0, 0.08], [0, 1]);
  const workflowScale = useTransform(scrollYProgress, [0, 0.08], [0.95, 1]);

  // All 4 steps appear together with stagger (0.08-0.2) - 扩展时间
  const step1Y = useTransform(scrollYProgress, [0.08, 0.12], [-30, 0]);
  const step1Opacity = useTransform(scrollYProgress, [0.08, 0.12], [0, 1]);

  const step2Y = useTransform(scrollYProgress, [0.1, 0.14], [-30, 0]);
  const step2Opacity = useTransform(scrollYProgress, [0.1, 0.14], [0, 1]);

  const step3Y = useTransform(scrollYProgress, [0.12, 0.16], [-30, 0]);
  const step3Opacity = useTransform(scrollYProgress, [0.12, 0.16], [0, 1]);

  const step4Y = useTransform(scrollYProgress, [0.14, 0.18], [-30, 0]);
  const step4Opacity = useTransform(scrollYProgress, [0.14, 0.18], [0, 1]);

  // Input box appears (0.25-0.32) - 调整时机
  const inputBoxOpacity = useTransform(scrollYProgress, [0.25, 0.32], [0, 1]);
  const inputBoxY = useTransform(scrollYProgress, [0.25, 0.32], [20, 0]);

  // Typing animation (0.35-0.5) with pause (0.5-0.55) - 扩展时间
  const typingProgress = useTransform(scrollYProgress, [0.35, 0.5], [0, 1]);
  const typingPause = useTransform(scrollYProgress, [0.5, 0.55], [1, 1]); // Pause at complete

  // Step 1 and 4 exit right (0.56-0.58) - 先让推特步骤退场
  const step1ExitX = useTransform(scrollYProgress, [0.56, 0.58], [0, 200]);
  const step1ExitOpacity = useTransform(scrollYProgress, [0.56, 0.58], [1, 0]);
  const step4ExitX = useTransform(scrollYProgress, [0.56, 0.58], [0, 200]);
  const step4ExitOpacity = useTransform(scrollYProgress, [0.56, 0.58], [1, 0]);

  // Step 2 and 3 move up (0.58-0.6) - 延迟上移动画，避免与退场重合
  const step2MoveY = useTransform(scrollYProgress, [0.58, 0.6], [0, -80]);
  const step3MoveY = useTransform(scrollYProgress, [0.58, 0.6], [0, -80]);

  // New steps appear after Twitter exits (0.59-0.62) - 推特退场后Reddit再出现
  const newStep1Y = useTransform(scrollYProgress, [0.59, 0.61], [-255, -290]); // Move to step 3's position
  const newStep1AppearOpacity = useTransform(scrollYProgress, [0.59, 0.61], [0, 1]);
  const newStep2Y = useTransform(scrollYProgress, [0.6, 0.62], [-255, -290]); // Move to step 4's position
  const newStep2AppearOpacity = useTransform(scrollYProgress, [0.6, 0.62], [0, 1]);

  // Sequential exit from top to bottom (0.63-0.7) - 从上到下依次退场
  const step2ExitX = useTransform(scrollYProgress, [0.63, 0.65], [0, 200]);
  const step2ExitOpacity = useTransform(scrollYProgress, [0.63, 0.65], [1, 0]);
  const step3ExitX = useTransform(scrollYProgress, [0.64, 0.66], [0, 200]);
  const step3ExitOpacity = useTransform(scrollYProgress, [0.64, 0.66], [1, 0]);
  const newStep1ExitX = useTransform(scrollYProgress, [0.65, 0.67], [0, 200]);
  const newStep1ExitOpacity = useTransform(scrollYProgress, [0.65, 0.67], [1, 0]);
  const newStep2ExitX = useTransform(scrollYProgress, [0.66, 0.68], [0, 200]);
  const newStep2ExitOpacity = useTransform(scrollYProgress, [0.66, 0.68], [1, 0]);
  const inputExitX = useTransform(scrollYProgress, [0.67, 0.7], [0, 200]);
  const inputExitOpacity = useTransform(scrollYProgress, [0.67, 0.7], [1, 0]);

  // 组合透明度变换
  const combinedStep2Opacity = useTransform([step2Opacity, step2ExitOpacity], ([step, exit]) =>
    Math.min(step as number, exit as number)
  );
  const combinedStep3Opacity = useTransform([step3Opacity, step3ExitOpacity], ([step, exit]) =>
    Math.min(step as number, exit as number)
  );
  const combinedInputOpacity = useTransform([inputBoxOpacity, inputExitOpacity], ([input, exit]) =>
    Math.min(input as number, exit as number)
  );
  const combinedNewStep1Opacity = useTransform([newStep1AppearOpacity, newStep1ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedNewStep2Opacity = useTransform([newStep2AppearOpacity, newStep2ExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );

  // Typing effect with pause
  useEffect(() => {
    const unsubscribe1 = typingProgress.on("change", (progress) => {
      const targetLength = Math.floor(progress * typingText.length);
      setDisplayedText(typingText.slice(0, targetLength));
    });

    const unsubscribe2 = typingPause.on("change", (pauseProgress) => {
      if (pauseProgress === 1) {
        setDisplayedText(typingText);
      }
    });

    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  }, [typingProgress, typingPause, typingText]);

  return (
    <div className="w-full relative flex items-center justify-center">
      {/* 视频容器 */}
      <motion.div
        className="absolute inset-0 z-20 flex items-center justify-center"
        style={{
          opacity: showVideo,
          x: useTransform(showVideo, [0, 1], [-100, 0]),
          filter: useTransform(showVideo, [0, 1], ["blur(4px)", "blur(0px)"]),
        }}
      >
        <video
          ref={videoRef}
          className="w-full h-full object-cover rounded-2xl"
          src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Controllable+workflow.webm"
          loop
          muted
          playsInline
        />
      </motion.div>

      {/* Demo Container */}
      <motion.div
        className="w-[700px] h-[500px] relative overflow-hidden"
        style={{
          opacity: useTransform(showVideo, [0, 0.1, 0.5, 1], [1, 1, 0.3, 0]),
        }}
        transition={{ duration: 0.5 }}
      >
        {/* Wireframe Container */}
        <motion.div
          className="w-full h-full flex items-center justify-center"
          style={{
            opacity: workflowOpacity,
            scale: workflowScale,
          }}
        >
          <div className="w-[700px] h-[500px] bg-white/10 rounded-2xl border border-white/10 p-6 flex flex-col gap-4">
            {/* Header wireframe */}
            <div className="h-8 bg-white/10 rounded-lg w-48"></div>

            {/* Step 1 - Abstract block */}
            <motion.div
              className="relative"
              style={{
                y: step1Y,
                opacity: useTransform(() => step1Opacity.get() * step1ExitOpacity.get()),
                x: step1ExitX,
              }}
            >
              <div className="h-[60px] bg-white/10 rounded-xl border border-white/10 p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-3 bg-white/10 rounded w-32 mb-2"></div>
                  <div className="h-2 bg-white/10 rounded w-24"></div>
                </div>
                <img src="https://api.iconify.design/logos:twitter.svg" alt="Twitter" className="w-6 h-6" />
              </div>
            </motion.div>

            {/* Step 2 - Abstract block */}
            <motion.div
              className="relative"
              style={{
                x: step2ExitX,
                y: useTransform(() => step2Y.get() + step2MoveY.get()),
                opacity: combinedStep2Opacity,
              }}
            >
              <div className="h-[60px] bg-white/10 rounded-xl border border-white/10 p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-3 bg-white/10 rounded w-28 mb-2"></div>
                  <div className="h-2 bg-white/10 rounded w-20"></div>
                </div>
                <div className="w-6 h-6 bg-white/10 rounded-full"></div>
              </div>
            </motion.div>

            {/* Step 3 - This will be edited */}
            <motion.div
              className="relative"
              style={{
                x: step3ExitX,
                y: useTransform(() => step3Y.get() + step3MoveY.get()),
                opacity: combinedStep3Opacity,
              }}
            >
              <div className="h-[60px] bg-white/10 rounded-xl border border-white/10 p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-3 bg-white/10 rounded w-36 mb-2"></div>
                  <div className="h-2 bg-white/10 rounded w-28"></div>
                </div>
                <div className="w-6 h-6 bg-white/10 rounded-full"></div>
              </div>
            </motion.div>

            {/* Step 4 - Abstract block */}
            <motion.div
              className="relative"
              style={{
                y: step4Y,
                opacity: useTransform(() => step4Opacity.get() * step4ExitOpacity.get()),
                x: step4ExitX,
              }}
            >
              <div className="h-[60px] bg-white/10 rounded-xl border border-white/10 p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-3 bg-white/10 rounded w-24 mb-2"></div>
                  <div className="h-2 bg-white/10 rounded w-20"></div>
                </div>
                <img src="https://api.iconify.design/logos:twitter.svg" alt="Twitter" className="w-6 h-6" />
              </div>
            </motion.div>

            {/* Input Box */}
            <motion.div
              className="relative"
              style={{
                x: inputExitX,
                y: inputBoxY,
                opacity: combinedInputOpacity,
              }}
            >
              <div className="h-[120px] bg-white/10 rounded-xl border border-white/10 p-3 flex items-start gap-3 pt-2">
                <div className="flex-1 relative">
                  <div className="text-sm text-gray-300 font-mono">
                    {displayedText}
                    <motion.span
                      className="inline-block w-0.5 h-5 bg-purple-400 ml-1 translate-y-1"
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.8, repeat: Infinity }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* New Step 1 */}
            <motion.div
              className="relative"
              style={{
                x: newStep1ExitX,
                y: newStep1Y,
                opacity: combinedNewStep1Opacity,
              }}
            >
              <div className="h-[60px] bg-white/10 rounded-xl border border-white/10 p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-3 bg-white/10 rounded w-40 mb-2"></div>
                  <div className="h-2 bg-white/10 rounded w-32"></div>
                </div>
                <img src="https://api.iconify.design/logos:reddit-icon.svg" alt="Reddit" className="w-6 h-6" />
              </div>
            </motion.div>

            {/* New Step 2 */}
            <motion.div
              className="relative"
              style={{
                x: newStep2ExitX,
                y: newStep2Y,
                opacity: combinedNewStep2Opacity,
              }}
            >
              <div className="h-[60px] bg-white/10 rounded-xl border border-white/10 p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-3 bg-white/10 rounded w-36 mb-2"></div>
                  <div className="h-2 bg-white/10 rounded w-28"></div>
                </div>
                <img src="https://api.iconify.design/logos:reddit-icon.svg" alt="Reddit" className="w-6 h-6" />
              </div>
            </motion.div>

            {/* Background pattern dots */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-8 right-8 w-12 h-12 border border-white/10 rounded-full"></div>
              <div className="absolute bottom-12 left-12 w-6 h-6 border border-white/10 rounded"></div>
              <div className="absolute top-1/2 right-16 w-4 h-4 border border-white/10 rounded-full"></div>
              <div className="absolute bottom-1/3 left-1/4 w-8 h-8 border border-white/10 rounded-lg"></div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
